import { useCallback, useEffect, useRef, useState } from "react";
import { CALENDAR_MIN_SLOT_MIN, type ScheduleEntry } from "@edu/shared";
import { eatClick, minuteAt } from "./drag-dom.ts";
import { edgeDrift, type EdgeDrift } from "./drag-edge.ts";
import {
  dropPlan,
  drawnMinutes,
  ghostLabel,
  grabMinutes,
  nextSnapStep,
  type DropPlan,
  type SnapStep,
} from "./entry-drag.ts";

/**
 * Состояние жеста «взял занятие рукой, положил на другую клетку».
 *
 * Своим файлом, а не внутри календаря: `CalendarGrid` и без жеста стоял у
 * потолка в 400 строк (раздел 9 CLAUDE.md). Здесь только состояние: что в руке,
 * куда целимся, когда содержимое едет само. Что при этом СЧИТАЕТСЯ, живёт в
 * `entry-drag.ts` и `drag-edge.ts` чистыми функциями с тестами, разговор с
 * настоящим DOM в `drag-dom.ts`, а что рисуется, в `CalendarDay.tsx` и стилях.
 *
 * Проект целиком в `docs/schedule-drag-design.md`.
 *
 * ⚠️ **Слушатели на ОКНЕ, а не `setPointerCapture`**, ровно как у переезда
 * плитки видео (`features/room/RoomVideo.tsx`): захват меняет цель событий, а
 * нам на каждом движении нужен настоящий элемент под указателем.
 *
 * ⚠️ **Мышь берёт занятие сразу, палец удержанием** (12.09.2026). До этого дня
 * жест был мышиным целиком: на телефоне занятие не двигалось вовсе, а нажатие
 * выделяло текст в коробке. Порог в четыре пикселя пальцу не годится, потому что
 * прокрутка сетки начинается с того же движения, что и перенос. Удержание их
 * разводит, и до его срабатывания `preventDefault` не зовётся ни разу: прокрутка
 * и короткое нажатие остаются ровно такими, какими были.
 */

/** Сколько пикселей надо проехать МЫШЬЮ, чтобы нажатие стало переносом. */
const START_PX = 4;
/** Сколько держать ПАЛЬЦЕМ, чтобы занятие оказалось в руке. Миллисекунды. */
const HOLD_MS = 450;
/**
 * Допуск пальца: меньше это дрожь на удержании, больше уже прокрутка. Восемь
 * пикселей, а не четыре, потому что палец дрожит сильнее мыши.
 */
const TOUCH_SLOP_PX = 8;
/** Доля новой скорости в сглаженной. Меньше значит спокойнее, но медленнее. */
const SPEED_MIX = 0.4;
/** Сколько пикселей содержимое проезжает за кадр, пока указатель у края. */
const EDGE_STEP_PX = 14;

interface Gesture {
  id: number;
  /** Палец либо перо: у них занятие берётся удержанием, а не движением. */
  touch: boolean;
  entry: ScheduleEntry;
  /** За сколько минут от начала занятия взяли коробку (раздел 4 проекта). */
  grabMin: number;
  sx: number;
  sy: number;
  /** Занятие в руке: с этой секунды это перенос, а не нажатие. */
  held: boolean;
  /**
   * После взятия указатель проехал дальше порога.
   *
   * ⚠️ Отдельно от `held` ради пальца: подержал и отпустил на месте это не
   * перенос, а нажатие, и карточка обязана открыться (`eatClick`). У мыши
   * признак поднимается вместе с `held`: взятие там и есть проезд.
   */
  drifted: boolean;
  /** Шаг привязки прямо сейчас. Начинаем с крупного: сперва выбирают день. */
  snap: SnapStep;
  /** Ниже этого шаг не опускается: у пальца четверть часа, у мыши пять минут. */
  floor: SnapStep;
  /**
   * Сглаженная скорость руки, пиксели в секунду. Сглаживание обязательно: мышь
   * шлёт события неровно, и по одному отрезку скорость скачет вдвое на ровном
   * движении, а вместе с ней скакал бы и шаг.
   */
  speed: number;
  lastX: number;
  lastY: number;
  lastAt: number;
}

/** Призрак занятия на будущем месте. Минуты в зоне СМОТРЯЩЕГО, как вся сетка. */
export interface DragGhost {
  date: string;
  startMin: number;
  endMin: number;
  /** «чт, 15:00». */
  label: string;
}

export interface CalendarDrag {
  /**
   * Занятие взяли, и порог пройден.
   *
   * ⚠️ Здесь закрывается ЛЮБАЯ открытая карточка занятия, а не только карточка
   * взятого: это 23rem непрозрачного фона поверх двух-трёх колонок недели, и
   * начало жеста она переживает.
   */
  onStart: () => void;
  /** Занятие отпустили над клеткой. */
  onDrop: (entry: ScheduleEntry, plan: DropPlan) => void;
}

/** Указатель ушёл от точки нажатия дальше допуска. */
function far(g: Gesture, x: number, y: number, slop: number): boolean {
  return Math.abs(x - g.sx) >= slop || Math.abs(y - g.sy) >= slop;
}

export function useCalendarDrag(zone: string, drag: CalendarDrag | null) {
  /**
   * Календарь для самохода у края: чью прокрутку двигать и от чьих кромок
   * мерить.
   *
   * ⚠️ Висит он на `.cal__body`, а не на `.cal` (17.09.2026), и с того же дня
   * это ОБЕ оси сразу. Тело календаря единственная коробка, у которой есть
   * прокрутка: вбок она переехала туда утром (шапка дней), вертикаль вечером
   * (календарь получил собственную высоту, `CalendarGrid`, решение 6). Ставь
   * ссылку на `.cal`, и не поедет ничего: ни `scrollLeft`, ни `scrollTop` у
   * него не двигают ничего вовсе, а кромки соврут на высоту строки дней.
   */
  const calRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [ghost, setGhost] = useState<DragGhost | null>(null);
  const gesture = useRef<Gesture | null>(null);
  /** Таймер удержания у пальца. Ноль означает «не заведён». */
  const hold = useRef(0);
  /** Куда едет содержимое, пока указатель у края. */
  const drift = useRef<EdgeDrift>({ x: 0, y: 0 });
  const raf = useRef(0);
  /**
   * Свежие `zone` и `drag` для слушателей окна: они висят с монтирования и
   * замкнули бы первые значения.
   */
  const live = useRef({ zone, drag });
  live.current = { zone, drag };

  const stopEdge = useCallback(() => {
    drift.current = { x: 0, y: 0 };
    if (raf.current !== 0) cancelAnimationFrame(raf.current);
    raf.current = 0;
  }, []);

  const rest = useCallback(() => {
    gesture.current = null;
    // Таймер удержания снимается и здесь (отпустили до срабатывания, нажали
    // Escape): иначе занятие взялось бы уже без пальца
    if (hold.current !== 0) clearTimeout(hold.current);
    hold.current = 0;
    stopEdge();
    setDragging(false);
    setGhost(null);
  }, [stopEdge]);

  /** Посчитать цель по точке и обновить призрак. Зовут и движение, и самоход. */
  const aim = useCallback((x: number, y: number) => {
    const g = gesture.current;
    if (g === null) return;
    const at = minuteAt(x, y);
    if (at === null) {
      setGhost(null);
      return;
    }
    const target = dropPlan(
      g.entry,
      live.current.zone,
      { date: at.date, startMin: at.min - g.grabMin },
      g.snap,
    ).target;
    const next: DragGhost = {
      date: target.viewerDate,
      startMin: target.viewerMin,
      endMin: target.viewerMin + drawnMinutes(g.entry),
      label: ghostLabel(target),
    };
    setGhost((prev) =>
      prev !== null && prev.date === next.date && prev.startMin === next.startMin ? prev : next,
    );
  }, []);

  /**
   * Занятие в руке. ⚠️ **Призрак ставится сразу, а не на первом движении:** у
   * пальца курсора нет, и единственный ответ на вопрос «взялось ли» это
   * картинка. Вибрация необязательна намеренно, на iOS её нет вовсе.
   */
  const take = useCallback(
    (x: number, y: number) => {
      const g = gesture.current;
      if (g === null) return;
      g.held = true;
      setDragging(true);
      live.current.drag?.onStart();
      if (g.touch) navigator.vibrate?.(15);
      aim(x, y);
    },
    [aim],
  );

  const tick = useCallback(() => {
    const g = gesture.current;
    const el = calRef.current;
    const to = drift.current;
    if (g === null || el === null || (to.x === 0 && to.y === 0)) {
      raf.current = 0;
      return;
    }
    // ⚠️ Обе оси едут ОДНОЙ коробкой, телом календаря. До 17.09.2026 (вечер)
    // вертикаль ехала окном (`window.scrollBy`), потому что страница и была
    // вертикальной прокруткой календаря. Теперь экран занимает ровно высоту
    // окна, то есть `window.scrollBy` не двигал бы НИЧЕГО, и занятие у нижней
    // кромки было бы не донести до вечерних часов (разбор в
    // `docs/schedule-calendar-design.md`, 2.5)
    if (to.x !== 0) el.scrollLeft += to.x * EDGE_STEP_PX;
    if (to.y !== 0) el.scrollTop += to.y * EDGE_STEP_PX;
    // ⚠️ Цель пересчитывается В ТОМ ЖЕ КАДРЕ: указатель стоит на месте, а
    // содержимое под ним уезжает, и призрак, посчитанный один раз на последнем
    // движении, показывал бы время, которого под пальцем уже нет
    aim(g.lastX, g.lastY);
    raf.current = requestAnimationFrame(tick);
  }, [aim]);

  /**
   * Слушатели вешаются на МОНТИРОВАНИЕ и висят, пока открыт экран. Нажатие
   * меняет только `ref`, то есть кадра не вызывает, и эффект, зависящий от
   * состояния «тащим», о начале жеста не узнал бы вовсе.
   */
  useEffect(() => {
    const move = (e: PointerEvent) => {
      const g = gesture.current;
      if (g === null || e.pointerId !== g.id) return;
      if (!g.held) {
        // Палец до срабатывания таймера: это либо прокрутка, либо нажатие, и
        // уехавший дальше допуска снимает жест ЦЕЛИКОМ, прокрутка уже пошла
        if (g.touch) {
          if (far(g, e.clientX, e.clientY, TOUCH_SLOP_PX)) rest();
          return;
        }
        if (!far(g, e.clientX, e.clientY, START_PX)) return;
        take(e.clientX, e.clientY);
        g.drifted = true;
      } else if (!g.drifted && far(g, e.clientX, e.clientY, TOUCH_SLOP_PX)) {
        g.drifted = true;
      }
      // Выделение текста, начатое до порога, иначе тянется за курсором и
      // подсвечивает половину недели
      window.getSelection()?.removeAllRanges();

      /*
        Шаг привязки от скорости руки: быстро значит выбираем день и час, медленно
        значит доводим. Разбор порогов, запаса на дрожь и пола у `nextSnapStep`.
      */
      const now = e.timeStamp;
      const dt = Math.max(now - g.lastAt, 1);
      const dist = Math.hypot(e.clientX - g.lastX, e.clientY - g.lastY);
      g.speed = g.speed * (1 - SPEED_MIX) + (dist / dt) * 1000 * SPEED_MIX;
      g.lastX = e.clientX;
      g.lastY = e.clientY;
      g.lastAt = now;
      g.snap = nextSnapStep(g.snap, g.speed, g.floor);

      const el = calRef.current;
      if (el !== null) {
        // Кромки берутся у той же коробки, которой кадр самохода двигает
        // прокрутку: полоса самохода обязана совпадать с тем, что едет
        drift.current = edgeDrift({ x: e.clientX, y: e.clientY }, el.getBoundingClientRect());
        const moving = drift.current.x !== 0 || drift.current.y !== 0;
        if (moving && raf.current === 0) raf.current = requestAnimationFrame(tick);
        if (!moving && raf.current !== 0) stopEdge();
      }

      aim(e.clientX, e.clientY);
    };

    /**
     * ⚠️ **Прокрутка отбирается ЗДЕСЬ, и слушатель висит ЗАРАНЕЕ.**
     *
     * И `touch-action`, и признак «в этой области есть блокирующий обработчик
     * касаний» браузер защёлкивает в момент `touchstart`: слушатель, повешенный
     * после срабатывания таймера удержания, прокрутку уже не отменит, Chrome
     * прокрутит в композиторе, не спрашивая. Поэтому непассивный слушатель висит
     * вместе с остальными и первой строкой выходит, если жеста в руке нет.
     * Цена названа честно: обработчик касаний висит на всём экране расписания,
     * то есть прокрутка там теряет право уехать в композитор. Дешевле, чем
     * `touch-action: none` на коробке занятия: тот отнял бы прокрутку у главного
     * способа смотреть расписание ВСЕГДА, а не на время жеста.
     */
    const scroll = (e: TouchEvent) => {
      const g = gesture.current;
      if (g === null || !g.touch || !g.held) return;
      e.preventDefault();
    };

    /** Долгое нажатие на Android иначе открывает своё меню посреди переноса. */
    const menu = (e: Event) => {
      const g = gesture.current;
      if (g === null || !g.touch) return;
      e.preventDefault();
    };

    const up = (e: PointerEvent) => {
      const g = gesture.current;
      if (g === null || e.pointerId !== g.id) return;
      const held = g;
      // Ни порога, ни удержания: это обычное нажатие, и карточка занятия обязана
      // открыться как раньше
      if (!held.held) {
        rest();
        return;
      }
      // ⚠️ Отмена это НЕ отпускание, и цель у неё пустая намеренно: у мыши
      // `pointercancel` почти не случался, а у пальца он штатен (звонок,
      // системный жест, второй палец), и общий путь переносил бы занятие на
      // клетку под указателем, хотя человек ничего не отпускал
      const at = e.type === "pointercancel" ? null : minuteAt(e.clientX, e.clientY);
      const drifted = held.drifted;
      rest();
      if (drifted) eatClick();
      // Мёртвая зона (шкала часов, шапка дня, мимо календаря): исход 4, ничего
      // не происходит вовсе
      if (at === null) return;
      live.current.drag?.onDrop(
        held.entry,
        dropPlan(
          held.entry,
          live.current.zone,
          { date: at.date, startMin: at.min - held.grabMin },
          held.snap,
        ),
      );
    };

    /** Escape посреди жеста отменяет его, как у любого перетаскивания. */
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || gesture.current === null) return;
      const drifted = gesture.current.drifted;
      rest();
      if (drifted) eatClick();
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    window.addEventListener("keydown", key);
    window.addEventListener("touchmove", scroll, { passive: false });
    window.addEventListener("contextmenu", menu);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      window.removeEventListener("keydown", key);
      window.removeEventListener("touchmove", scroll);
      window.removeEventListener("contextmenu", menu);
      if (raf.current !== 0) cancelAnimationFrame(raf.current);
      if (hold.current !== 0) clearTimeout(hold.current);
    };
  }, [aim, rest, stopEdge, take, tick]);

  /**
   * Свойства коробки занятия: одно нажатие, и то не всякое. ⚠️ Атрибута
   * `draggable` тут нет намеренно: он на `<button>` не работает в Chrome и
   * Safari (пункт 13 раздела 18 проекта), а браузеру, который его послушается,
   * мы отдали бы второй жест поверх своего.
   */
  const boxProps = (entry: ScheduleEntry, piece: { startMin: number; endMin: number }) => {
    if (drag === null) return {};
    return {
      onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
        // Только основная кнопка мыши (правой открывают меню, средней метят в
        // прокрутку) и только первый палец: второй это щипок масштаба
        if (e.button !== 0 || !e.isPrimary) return;
        const touch = e.pointerType !== "mouse";
        const box = e.currentTarget.getBoundingClientRect();
        gesture.current = {
          id: e.pointerId,
          touch,
          entry,
          // Нарисованная высота, а не длительность: короткое занятие рисуется
          // минимальной коробкой, и хват считается по ней же
          grabMin: grabMinutes(
            e.clientY - box.top,
            box.height,
            Math.max(piece.endMin - piece.startMin, CALENDAR_MIN_SLOT_MIN),
          ),
          sx: e.clientX,
          sy: e.clientY,
          held: false,
          drifted: false,
          // Начинаем с крупного шага: жест начинается с выбора дня, а не минуты
          snap: 30,
          floor: touch ? 15 : 5,
          speed: 0,
          lastX: e.clientX,
          lastY: e.clientY,
          lastAt: e.timeStamp,
        };
        if (!touch) return;
        // ⚠️ До срабатывания таймера `preventDefault` не зовётся НИ РАЗУ: пока
        // палец не продержали, страница и календарь прокручиваются как обычно
        const x = e.clientX;
        const y = e.clientY;
        hold.current = window.setTimeout(() => {
          hold.current = 0;
          const g = gesture.current;
          if (g === null) return;
          // Отсчёт скорости руки идёт со взятия, а не с нажатия: иначе полсекунды
          // неподвижности сделали бы первое движение медленным
          g.lastAt = performance.now();
          take(x, y);
        }, HOLD_MS);
      },
    };
  };

  return { calRef, dragging, ghost, boxProps };
}
